import moment from 'moment-timezone';

import { generateAcceptedPaymentsString } from 'shared/helpers/dispensaries';

import { countryMapToShortened } from 'shared/core/constants/geography';

const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const militaryTime = (hours) => {
  const normalizedHours = hours?.replace(/\s*\(Next Day\)\s*$/i, '');
  const parsedHours = moment(normalizedHours, `h:mm A`, true);

  return parsedHours.isValid() ? parsedHours.format(`HH:mm`) : null;
};

const buildOpeningHoursSpecification = (hours = {}) =>
  DAYS_OF_WEEK.flatMap((dayOfWeek) => {
    const dayHours = hours[dayOfWeek];

    if (!dayHours?.active) {
      return [];
    }

    const is24Hours = dayHours.start === 'open' && dayHours.end === 'open';
    const opens = is24Hours ? '00:00' : militaryTime(dayHours.start);
    const closes = is24Hours ? '23:59' : militaryTime(dayHours.end);

    if (!opens || !closes) {
      return [];
    }

    return {
      '@type': `OpeningHoursSpecification`,
      dayOfWeek: `https://schema.org/${dayOfWeek}`,
      opens,
      closes,
    };
  });

const compact = (value) => {
  if (Array.isArray(value)) {
    return [...new Set(value.filter((item) => item !== null && item !== undefined && item !== ''))];
  }

  return Object.fromEntries(
    Object.entries(value).filter(([, item]) => {
      if (item === null || item === undefined || item === '') {
        return false;
      }

      return !Array.isArray(item) || item.length > 0;
    })
  );
};

const buildDispensaryJsonLd = (dispensaryData, storeFrontAbsoluteUrl) => {
  // The dispensary's own page IS the store front's home -- no suffix to append.
  const absoluteUrl = storeFrontAbsoluteUrl;
  const {
    name,
    location = {},
    description,
    listImage,
    bannerImage,
    logoImage,
    embeddedLogoImage,
    SpecialLogoImage,
    phone,
    hoursSettings,
    pickupHours,
  } = dispensaryData;
  const { ln1, ln2, city, state, zipcode, geometry, country } = location;
  const [longitude, latitude] = geometry?.coordinates ?? [];
  const countryCode = countryMapToShortened[country] || country;
  const hasCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude);

  const paymentAccepted = generateAcceptedPaymentsString(dispensaryData);
  const openingHoursSpecification = buildOpeningHoursSpecification(hoursSettings?.inStorePickup?.hours ?? pickupHours);
  const images = compact([listImage, bannerImage, logoImage, embeddedLogoImage, SpecialLogoImage]);

  return compact({
    '@context': `https://schema.org`,
    '@type': `Store`,
    '@id': absoluteUrl ? `${absoluteUrl}#store` : undefined,
    name,
    description,
    url: absoluteUrl,
    telephone: phone,
    logo: logoImage,
    image: images,
    address: compact({
      '@type': `PostalAddress`,
      streetAddress: [ln1, ln2].filter(Boolean).join(' '),
      addressLocality: city,
      addressRegion: state,
      postalCode: zipcode,
      addressCountry: countryCode,
    }),
    geo: hasCoordinates
      ? {
          '@type': `GeoCoordinates`,
          latitude,
          longitude,
        }
      : undefined,
    hasMap: hasCoordinates
      ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${latitude},${longitude}`)}`
      : undefined,
    currenciesAccepted: countryCode === 'CA' ? 'CAD' : 'USD',
    paymentAccepted,
    openingHoursSpecification,
  });
};

export default buildDispensaryJsonLd;
